import {
  COMMENT_MAX_LENGTH,
  type LiveComment,
  type LiveRejectReason,
  rejectReasonText,
} from '@shared/live.ts'
import { formatJstClock } from '@shared/time.ts'
import { type Component, createEffect, createSignal, For, Show } from 'solid-js'

type Props = {
  comments: LiveComment[]
  connected: boolean
  /** サーバーから割り当てられた自分の表示名 */
  name: string
  rejection: LiveRejectReason | null
  /** 運営がコメントを止めているか */
  muted: boolean
  onPost: (text: string) => boolean
}

/**
 * 放送中の番組の実況（要件定義 17 章）。
 *
 * YouTube Player には重ねない。
 * 要件定義 3.3「Player の UI を不必要に隠さない」/ 14 章「帰属表示を不当に隠さない」に
 * 反するため、ニコニコ風のオーバーレイは採用していない。
 */
export const CommentPanel: Component<Props> = (props) => {
  const [draft, setDraft] = createSignal('')
  let list!: HTMLUListElement
  /** 一番下を見ているときだけ自動で追従する */
  let stickToBottom = true

  const remaining = () => COMMENT_MAX_LENGTH - [...draft()].length
  const canPost = () => props.connected && !props.muted

  createEffect(() => {
    // comments を読んで、増えたら追従する
    props.comments.length
    if (stickToBottom && list) list.scrollTop = list.scrollHeight
  })

  const submit = (event: Event): void => {
    event.preventDefault()
    const text = draft().trim()
    if (text === '') return
    if (props.onPost(text)) {
      setDraft('')
      stickToBottom = true
    }
  }

  return (
    <section class="flex min-h-0 flex-1 flex-col">
      {/* 狭い画面ではタブが見出しを兼ねるので、この行ごと畳む */}
      <div class="mb-2 hidden shrink-0 items-baseline justify-between lg:flex">
        <h2 class="text-[11px] font-semibold tracking-[0.18em] text-ch-muted">COMMENTS</h2>
        <span class="text-[10px] text-ch-dim">番組が切り替わると消えます</span>
      </div>

      <ul
        ref={list}
        onScroll={() => {
          const distance = list.scrollHeight - list.scrollTop - list.clientHeight
          stickToBottom = distance < 24
        }}
        class="min-h-0 flex-1 space-y-1.5 overflow-y-auto rounded-ch border border-ch-border bg-ch-surface p-3"
      >
        <Show
          when={props.comments.length > 0}
          fallback={
            <li class="grid h-full place-items-center text-center text-xs text-ch-dim">
              {props.connected ? 'まだコメントがありません' : '接続しています…'}
            </li>
          }
        >
          <For each={props.comments}>
            {(comment) => (
              <li class="text-sm leading-snug">
                <span class="mr-1.5 font-mono text-[10px] text-ch-dim tabular">
                  {formatJstClock(comment.at)}
                </span>
                <span
                  class="mr-1.5 text-xs"
                  classList={{
                    'text-ch-accent': comment.name === props.name,
                    'text-ch-muted': comment.name !== props.name,
                  }}
                >
                  {comment.name}
                </span>
                <span class="break-words">{comment.text}</span>
              </li>
            )}
          </For>
        </Show>
      </ul>

      <form onSubmit={submit} class="mt-2 flex shrink-0 gap-2">
        <input
          type="text"
          value={draft()}
          onInput={(event) => setDraft(event.currentTarget.value)}
          disabled={!canPost()}
          maxLength={COMMENT_MAX_LENGTH}
          placeholder={
            props.muted
              ? 'いまコメントを受け付けていません'
              : props.connected
                ? 'いまの番組について書く'
                : '接続しています…'
          }
          class="min-w-0 flex-1 rounded-ch border border-ch-border bg-ch-elevated px-3 py-2 text-sm placeholder:text-ch-dim disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!canPost() || draft().trim() === ''}
          class="shrink-0 rounded-ch bg-ch-elevated px-3 py-2 text-sm font-semibold hover:bg-ch-border disabled:opacity-40"
        >
          送信
        </button>
      </form>

      <div class="mt-1 hidden shrink-0 items-baseline justify-between text-[10px] sm:flex">
        <span class="text-ch-dim">
          <Show when={props.name !== ''}>あなたの名前: {props.name}</Show>
        </span>
        <span classList={{ 'text-ch-onair': remaining() < 0, 'text-ch-dim': remaining() >= 0 }}>
          {remaining()}
        </span>
      </div>

      <Show when={props.rejection}>
        {(reason) => <p class="mt-1 text-xs text-ch-onair">{rejectReasonText(reason())}</p>}
      </Show>
    </section>
  )
}
